import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import type { AuthUser } from '@/types/api'
import { getInstalledPwaRole, loginRouteForInstalledRole } from '@/lib/pwa-role'
import { signInPageAction } from '@/lib/login-pages'
import { endSession } from '@/lib/session'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      // The name belongs on the page, not on the layout: a navigation by name
      // (router.push({ name: 'home' })) matches only the named record, and Vue Router does not
      // add the empty-path child for a name-based resolve the way it does for a URL. With the
      // name on the parent, that navigation matched the layout alone, so <RouterView /> had no
      // match and <main> stayed empty until a full reload resolved the URL instead.
      path: '/',
      component: () => import('@/layouts/PublicLayout.vue'),
      children: [
        { path: '', name: 'home', component: () => import('@/pages/HomePage.vue') },
        { path: 'logistics', redirect: { name: 'home' } },
        { path: 'contact', redirect: '/logistics' },
        { path: 'download/:role(agent|courier|customer)?', name: 'download-app', component: () => import('@/pages/DownloadAppPage.vue') },
        { path: 'return-request', name: 'public-return-request', component: () => import('@/pages/PublicReturnRequestPage.vue') },
        { path: 'track/:ref', name: 'public-track', component: () => import('@/pages/PublicTrackingPage.vue') }
      ]
    },
    {
      path: '/admin/login',
      component: () => import('@/layouts/AuthLayout.vue'),
      meta: { guestOnly: true, loginRoles: ['admin', 'field_manager'] },
      children: [{ path: '', name: 'admin-login', component: () => import('@/pages/admin/AdminLoginPage.vue') }]
    },
    {
      path: '/agent/login',
      component: () => import('@/layouts/AuthLayout.vue'),
      meta: { guestOnly: true, loginRoles: ['agent'] },
      children: [{ path: '', name: 'agent-login', component: () => import('@/pages/auth/FieldLoginPage.vue') }]
    },
    {
      path: '/courier/login',
      component: () => import('@/layouts/AuthLayout.vue'),
      meta: { guestOnly: true, loginRoles: ['livreur'] },
      children: [{ path: '', name: 'courier-login', component: () => import('@/pages/auth/FieldLoginPage.vue') }]
    },
    {
      path: '/partner/login',
      component: () => import('@/layouts/AuthLayout.vue'),
      meta: { guestOnly: true, loginRoles: ['partner'] },
      children: [{ path: '', name: 'partner-login', component: () => import('@/pages/partner/PartnerLoginPage.vue') }]
    },
    {
      path: '/user/login',
      component: () => import('@/layouts/BlankLayout.vue'),
      meta: { guestOnly: true, loginRoles: ['platform', 'user'] },
      children: [{ path: '', name: 'user-login', component: () => import('@/pages/user/UserLoginPage.vue') }]
    },
    {
      path: '/admin',
      component: () => import('@/layouts/AdminLayout.vue'),
      meta: { roles: ['admin', 'field_manager'] },
      children: [
        { path: 'dashboard', name: 'admin-dashboard', component: () => import('@/pages/admin/AdminDashboardPage.vue') },
        { path: 'packages', name: 'admin-packages', component: () => import('@/pages/admin/AdminPackagesPage.vue') },
        { path: 'packages/create', name: 'admin-package-create', component: () => import('@/pages/admin/AdminPackageCreatePage.vue') },
        { path: 'packages/:id', name: 'admin-package-detail', component: () => import('@/pages/admin/AdminPackageDetailPage.vue') },
        { path: 'return-requests', name: 'admin-return-requests', component: () => import('@/pages/admin/AdminReturnRequestsPage.vue') },
        { path: 'return-incidents', name: 'admin-return-incidents', component: () => import('@/pages/admin/AdminReturnIncidentsPage.vue'), meta: { roles: ['admin'] } },
        { path: 'cancellation-requests', name: 'admin-cancellation-requests', component: () => import('@/pages/admin/AdminCancellationRequestsPage.vue'), meta: { roles: ['admin'] } },
        { path: 'order-requests', name: 'admin-order-requests', component: () => import('@/pages/admin/AdminOrderRequestsPage.vue') },
        { path: 'order-requests/:id', name: 'admin-order-request-detail', component: () => import('@/pages/admin/AdminOrderRequestDetailPage.vue') },
        { path: 'exceptions', name: 'admin-exceptions', component: () => import('@/pages/admin/AdminExceptionsPage.vue') },
        { path: 'agents', name: 'admin-agents', component: () => import('@/pages/admin/AdminAgentsPage.vue') },
        { path: 'agents/create', name: 'admin-agent-create', component: () => import('@/pages/admin/AdminAgentCreatePage.vue') },
        { path: 'agents/:id', name: 'admin-agent-detail', component: () => import('@/pages/admin/AdminAgentDetailPage.vue') },
        { path: 'residences', name: 'admin-residences', component: () => import('@/pages/admin/AdminResidencesPage.vue') },
        { path: 'partners', name: 'admin-partners', component: () => import('@/pages/admin/AdminPartnersPage.vue') },
        { path: 'partners/:id', name: 'admin-partner-detail', component: () => import('@/pages/admin/AdminPartnerDetailPage.vue') },
        { path: 'customers', name: 'admin-customers', component: () => import('@/pages/admin/AdminCustomersPage.vue') },
        { path: 'customers/:id', name: 'admin-customer-detail', component: () => import('@/pages/admin/AdminCustomerDetailPage.vue') },
        { path: 'livreurs', name: 'admin-livreurs', component: () => import('@/pages/admin/AdminLivreursPage.vue') },
        { path: 'livreurs/:id', name: 'admin-livreur-detail', component: () => import('@/pages/admin/AdminLivreurDetailPage.vue') },
        { path: 'audit', name: 'admin-audit', component: () => import('@/pages/admin/AuditViewerPage.vue') },
        { path: 'settlements', name: 'admin-settlements', component: () => import('@/pages/admin/SettlementDashboardPage.vue') },
        { path: 'settlements/:id', name: 'admin-settlement-detail', component: () => import('@/pages/admin/SettlementDetailPage.vue') },
        { path: 'withdrawals', name: 'admin-withdrawals', component: () => import('@/pages/admin/AdminWithdrawalsPage.vue'), meta: { roles: ['admin'] } },
        { path: 'platform-config', name: 'admin-platform-config', component: () => import('@/pages/admin/AdminPlatformConfigPage.vue'), meta: { roles: ['admin'] } },
        { path: 'profile', name: 'admin-profile', component: () => import('@/pages/admin/AdminProfilePage.vue') }
      ]
    },
    {
      path: '/agent/onboarding',
      component: () => import('@/layouts/BlankLayout.vue'),
      meta: { roles: ['agent'], pendingOnly: true },
      children: [{ path: '', name: 'agent-onboarding', component: () => import('@/pages/agent/AgentOnboardingPage.vue') }]
    },
    {
      path: '/agent',
      component: () => import('@/layouts/MobileOpsLayout.vue'),
      meta: { roles: ['agent'] },
      children: [
        { path: 'dashboard', name: 'agent-dashboard', component: () => import('@/pages/agent/AgentDashboardPage.vue'), meta: { title: 'pageTitles.home' } },
        { path: 'parcels', name: 'agent-parcels', component: () => import('@/pages/field/FieldParcelsPage.vue'), meta: { title: 'fieldJourney.parcels' } },
        { path: 'parcels/:id', name: 'agent-parcel', component: () => import('@/pages/field/FieldParcelJourneyPage.vue'), meta: { title: 'pageTitles.packageDetail', parent: 'agent-parcels' } },
        { path: 'scanner', name: 'agent-scanner', component: () => import('@/pages/field/FieldScanPage.vue'), meta: { title: 'pageTitles.scanner', parent: 'agent-dashboard' } },
        { path: 'map', name: 'agent-map', component: () => import('@/pages/agent/AgentParcelsMapPage.vue'), meta: { title: 'pageTitles.agentMap', parent: 'agent-dashboard' } },
        { path: 'packages', name: 'agent-packages', component: () => import('@/pages/agent/AgentPackagesPage.vue'), meta: { title: 'pageTitles.packages' } },
        { path: 'scan', name: 'agent-scan', component: () => import('@/pages/agent/AgentScanPage.vue'), meta: { title: 'pageTitles.scan', parent: 'agent-dashboard' } },
        { path: 'create-package', name: 'agent-create-package', component: () => import('@/pages/agent/AgentCreatePackagePage.vue'), meta: { title: 'pageTitles.createPackage', parent: 'agent-dashboard' } },
        { path: 'handovers', name: 'agent-handovers', component: () => import('@/pages/agent/AgentHandoversPage.vue'), meta: { title: 'pageTitles.handovers', parent: 'agent-dashboard' } },
        { path: 'handovers/:id', name: 'agent-handover-detail', component: () => import('@/pages/agent/AgentHandoverDetailPage.vue'), meta: { title: 'pageTitles.handoverDetail', parent: 'agent-handovers' } },
        { path: 'pickups', name: 'agent-pickups', component: () => import('@/pages/agent/AgentPickupsPage.vue'), meta: { title: 'pageTitles.pickups' } },
        { path: 'pickups/scan', name: 'agent-pickup-scan', component: () => import('@/pages/agent/AgentPickupScanPage.vue'), meta: { title: 'pageTitles.pickupScan', parent: 'agent-pickups' } },
        { path: 'pickups/handover', name: 'agent-pickup-handover', component: () => import('@/pages/agent/AgentPickupHandoverPage.vue'), meta: { title: 'pageTitles.pickupHandover', parent: 'agent-pickups' } },
        { path: 'pickups/sessions/:id', name: 'agent-pickup-session-detail', component: () => import('@/pages/agent/AgentPickupSessionDetailPage.vue'), meta: { title: 'pageTitles.pickupSession', parent: 'agent-pickups' } },
        { path: 'sender-return', name: 'agent-sender-return', component: () => import('@/pages/agent/AgentSenderReturnPage.vue'), meta: { title: 'pageTitles.senderReturn', parent: 'agent-returns' } },
        { path: 'returns', name: 'agent-returns', component: () => import('@/pages/agent/AgentReturnsPage.vue'), meta: { title: 'pageTitles.returns' } },
        // A parcel opens on its journey screen; the full old page is one tap away under "Full details".
        { path: 'packages/:id', name: 'agent-package-detail', redirect: (to) => ({ name: 'agent-parcel', params: to.params, query: to.query }) },
        { path: 'packages/:id/full', name: 'agent-package-full', component: () => import('@/pages/agent/AgentPackageDetailPage.vue'), meta: { title: 'pageTitles.packageDetail', parent: 'agent-parcels' } },
        { path: 'pod', name: 'agent-pod', component: () => import('@/pages/agent/AgentPodCapturePage.vue'), meta: { title: 'pageTitles.pod', parent: 'agent-packages' } },
        { path: 'bulk-pod', name: 'agent-bulk-pod', component: () => import('@/pages/agent/AgentBulkPodCapturePage.vue'), meta: { title: 'pageTitles.bulkPod', parent: 'agent-packages' } },
        { path: 'return-deposit', name: 'agent-return-deposit', component: () => import('@/pages/agent/AgentReturnDepositPage.vue'), meta: { title: 'pageTitles.returnDeposit', parent: 'agent-returns' } },
        { path: 'exception', name: 'agent-exception', component: () => import('@/pages/agent/AgentExceptionPage.vue'), meta: { title: 'pageTitles.exception', parent: 'agent-packages' } },
        { path: 'sync', name: 'agent-sync', component: () => import('@/pages/agent/AgentSyncStatusPage.vue'), meta: { title: 'pageTitles.sync', parent: 'agent-dashboard' } },
        { path: 'history', name: 'agent-history', component: () => import('@/pages/field/FieldHistoryPage.vue'), meta: { title: 'activityHistory.title', parent: 'agent-dashboard' } },
        { path: 'wallet', name: 'agent-wallet', component: () => import('@/pages/agent/AgentWalletPage.vue'), meta: { title: 'pageTitles.wallet', parent: 'agent-dashboard' } },
        { path: 'profile', name: 'agent-profile', component: () => import('@/pages/agent/AgentProfilePage.vue'), meta: { title: 'pageTitles.profile', parent: 'agent-dashboard' } }
      ]
    },
    {
      path: '/courier',
      component: () => import('@/layouts/MobileOpsLayout.vue'),
      meta: { roles: ['livreur'] },
      children: [
        { path: '', redirect: { name: 'courier-job' } },
        { path: 'job', name: 'courier-job', component: () => import('@/pages/courier/CourierParcelsMapPage.vue'), meta: { title: 'pageTitles.home' } },
        { path: 'dashboard', name: 'courier-dashboard', component: () => import('@/pages/courier/CourierDashboardPage.vue'), meta: { title: 'pageTitles.home' } },
        { path: 'parcels', name: 'courier-parcels', component: () => import('@/pages/field/FieldParcelsPage.vue'), meta: { title: 'fieldJourney.parcels' } },
        { path: 'parcels/:id', name: 'courier-parcel', component: () => import('@/pages/field/FieldParcelJourneyPage.vue'), meta: { title: 'pageTitles.packageDetail', parent: 'courier-parcels' } },
        { path: 'scanner', name: 'courier-scanner', component: () => import('@/pages/field/FieldScanPage.vue'), meta: { title: 'pageTitles.scanner', parent: 'courier-dashboard' } },
        // The parcel map is the courier's home now.
        { path: 'map', name: 'courier-map', redirect: { name: 'courier-job' } },
        { path: 'packages/:id', name: 'courier-package-detail', redirect: (to) => ({ name: 'courier-parcel', params: to.params, query: to.query }) },
        { path: 'packages/:id/full', name: 'courier-package-full', component: () => import('@/pages/courier/CourierPackageDetailPage.vue'), meta: { title: 'pageTitles.packageDetail', parent: 'courier-parcels' } },
        { path: 'drop', name: 'courier-drop', component: () => import('@/pages/courier/CourierDropPage.vue'), meta: { title: 'pageTitles.drop', parent: 'courier-drop-sessions' } },
        { path: 'drop-sessions', name: 'courier-drop-sessions', component: () => import('@/pages/courier/CourierDropSessionsPage.vue'), meta: { title: 'pageTitles.dropSessions' } },
        { path: 'drop-sessions/:id', name: 'courier-drop-session-detail', component: () => import('@/pages/courier/CourierDropSessionDetailPage.vue'), meta: { title: 'pageTitles.dropSessionDetail', parent: 'courier-drop-sessions' } },
        { path: 'pickups', name: 'courier-pickups', component: () => import('@/pages/courier/CourierPickupsPage.vue'), meta: { title: 'pageTitles.collections' } },
        { path: 'pickups/sessions/:id', name: 'courier-pickup-session-detail', component: () => import('@/pages/courier/CourierPickupSessionDetailPage.vue'), meta: { title: 'pageTitles.collectionDetail', parent: 'courier-pickups' } },
        { path: 'returns', name: 'courier-returns', component: () => import('@/pages/agent/AgentReturnsPage.vue'), meta: { title: 'pageTitles.returns' } },
        { path: 'return-collect', name: 'courier-return-collect', component: () => import('@/pages/courier/CourierReturnCollectPage.vue'), meta: { title: 'pageTitles.returnCollect', parent: 'courier-returns' } },
        { path: 'sender-return', name: 'courier-sender-return', component: () => import('@/pages/courier/CourierSenderReturnPage.vue'), meta: { title: 'pageTitles.senderReturn', parent: 'courier-dashboard' } },
        { path: 'exception', name: 'courier-exception', component: () => import('@/pages/courier/CourierExceptionPage.vue'), meta: { title: 'pageTitles.exception', parent: 'courier-dashboard' } },
        { path: 'sync', name: 'courier-sync', component: () => import('@/pages/agent/AgentSyncStatusPage.vue'), meta: { title: 'pageTitles.sync', parent: 'courier-dashboard' } },
        { path: 'history', name: 'courier-history', component: () => import('@/pages/field/FieldHistoryPage.vue'), meta: { title: 'activityHistory.title', parent: 'courier-dashboard' } },
        { path: 'wallet', name: 'courier-wallet', component: () => import('@/pages/courier/CourierWalletPage.vue'), meta: { title: 'pageTitles.wallet', parent: 'courier-dashboard' } },
        { path: 'profile', name: 'courier-profile', component: () => import('@/pages/courier/CourierProfilePage.vue'), meta: { title: 'pageTitles.profile', parent: 'courier-dashboard' } }
      ]
    },
    {
      path: '/partner',
      component: () => import('@/layouts/PartnerLayout.vue'),
      meta: { roles: ['partner'] },
      children: [
        { path: '', redirect: '/partner/dashboard' },
        { path: 'dashboard', name: 'partner-dashboard', component: () => import('@/pages/partner/PartnerDashboardPage.vue') },
        { path: 'packages', name: 'partner-packages', component: () => import('@/pages/partner/PartnerPackagesPage.vue') },
        { path: 'returns', name: 'partner-returns', component: () => import('@/pages/partner/PartnerReturnsPage.vue') },
        { path: 'packages/create', name: 'partner-package-create', component: () => import('@/pages/partner/PartnerPackageCreatePage.vue') },
        { path: 'packages/:id', name: 'partner-package-detail', component: () => import('@/pages/partner/PartnerPackageDetailPage.vue') },
        { path: 'residences-map', name: 'partner-residences-map', component: () => import('@/pages/partner/PartnerResidencesMapPage.vue') },
        { path: 'credit', name: 'partner-credit', component: () => import('@/pages/partner/PartnerCreditPage.vue') },
        { path: 'reports', name: 'partner-reports', component: () => import('@/pages/partner/PartnerReportsPage.vue') },
        { path: 'profile', name: 'partner-profile', component: () => import('@/pages/partner/PartnerProfilePage.vue') }
      ]
    },
    {
      path: '/user',
      component: () => import('@/layouts/UserLayout.vue'),
      meta: { roles: ['user'] },
      children: [
        { path: '', redirect: '/user/dashboard' },
        { path: 'dashboard', name: 'user-dashboard', component: () => import('@/pages/user/UserDashboardPage.vue') },
        { path: 'profile', name: 'user-profile', component: () => import('@/pages/user/UserDashboardPage.vue') }
      ]
    },
    {
      path: '/platform',
      component: () => import('@/layouts/PlatformLayout.vue'),
      meta: { roles: ['platform'] },
      children: [
        { path: '', redirect: '/platform/dashboard' },
        { path: 'scanner', name: 'platform-scanner', component: () => import('@/pages/platform/PlatformScanPage.vue'), meta: { title: 'pageTitles.scanner', parent: 'platform-dashboard' } },
        { path: 'dashboard', name: 'platform-dashboard', component: () => import('@/pages/platform/PlatformDashboardPage.vue'), meta: { title: 'pageTitles.home' } },
        { path: 'requests', name: 'platform-requests', component: () => import('@/pages/platform/PlatformRequestsPage.vue'), meta: { title: 'fieldJourney.parcels' } },
        { path: 'requests/new', name: 'platform-request-create', component: () => import('@/pages/platform/PlatformRequestCreatePage.vue'), meta: { title: 'pageTitles.requestNew', parent: 'platform-dashboard' } },
        { path: 'journey/:id', name: 'platform-journey', component: () => import('@/pages/platform/PlatformJourneyPage.vue'), meta: { title: 'pageTitles.requestDetail', parent: 'platform-dashboard' } },
        { path: 'requests/:id', name: 'platform-request-detail', component: () => import('@/pages/platform/PlatformRequestDetailPage.vue'), meta: { title: 'pageTitles.requestDetail', parent: 'platform-requests' } },
        { path: 'return-requests', name: 'platform-return-requests', component: () => import('@/pages/platform/PlatformReturnRequestsPage.vue'), meta: { title: 'pageTitles.returns', parent: 'platform-dashboard' } },
        { path: 'pickups', name: 'platform-pickups', component: () => import('@/pages/platform/PlatformPickupActionsPage.vue'), meta: { title: 'pageTitles.dropOffs', parent: 'platform-dashboard' } },
        { path: 'payments', name: 'platform-payments', component: () => import('@/pages/platform/PlatformPaymentsPage.vue'), meta: { title: 'pageTitles.payments' } },
        // The old console lived at /credit. Kept as a redirect so a bookmark does not 404.
        { path: 'credit', redirect: '/platform/payments' },
        { path: 'packages', name: 'platform-packages', component: () => import('@/pages/platform/PlatformPackagesPage.vue'), meta: { title: 'pageTitles.packages' } },
        { path: 'packages/:id', name: 'platform-package-detail', component: () => import('@/pages/platform/PlatformPackageDetailPage.vue'), meta: { title: 'pageTitles.packageDetail', parent: 'platform-packages' } },
      ]
    },
    {
      path: '/',
      component: () => import('@/layouts/BlankLayout.vue'),
      children: [
        { path: 'offline', name: 'offline', component: () => import('@/pages/OfflinePage.vue') },
        { path: '404', name: 'not-found', component: () => import('@/pages/NotFoundPage.vue') },
        { path: 'auth/sso/callback', name: 'sso-callback', component: () => import('@/pages/SsoCallbackPage.vue') },
        { path: ':pathMatch(.*)*', redirect: '/404' }
      ]
    }
  ],
  scrollBehavior: () => ({ top: 0 })
})

export function dashboardFor(role: AuthUser['role'] | null, status?: AuthUser['status'] | null) {
  if (role === 'admin' || role === 'field_manager') return { name: 'admin-dashboard' }
  if (role === 'agent') return status === 'pending' ? { name: 'agent-onboarding' } : { name: 'agent-dashboard' }
  if (role === 'livreur') return { name: 'courier-job' }
  if (role === 'partner') return { name: 'partner-dashboard' }
  if (role === 'user') return { name: 'user-dashboard' }
  if (role === 'platform') return { name: 'platform-dashboard' }
  return { name: 'home' }
}

export function loginForPath(path: string) {
  if (path.startsWith('/admin')) return { name: 'admin-login' }
  if (path.startsWith('/courier')) return { name: 'courier-login' }
  if (path.startsWith('/partner')) return { name: 'partner-login' }
  if (path.startsWith('/user')) return { name: 'user-login' }
  if (path.startsWith('/platform')) return { name: 'user-login' }
  return { name: 'agent-login' }
}

router.beforeEach((to) => {
  const auth = useAuthStore()
  const installedRole = getInstalledPwaRole()
  const requiredRoleGroups = to.matched
    .map((route) => (route.meta.roles || []) as AuthUser['role'][])
    .filter((roles) => roles.length > 0)
  const guestOnly = to.matched.some((route) => route.meta.guestOnly)
  const pendingOnly = to.matched.some((route) => route.meta.pendingOnly)

  if (installedRole) {
    // An installed app decides only where a BARE LAUNCH lands. It does not police the rest of the
    // navigation, and it must never discard a valid session: one person can legitimately hold more
    // than one role (a guardian who also receives parcels), and clearing the session on a role
    // mismatch silently logged them out and dropped them on this app's own login screen — which is
    // how you got stuck in the agent app after signing in as a customer.
    if (to.path === '/' || to.name === 'home') {
      return auth.isAuthenticated
        ? dashboardFor(auth.role, auth.user?.status)
        : loginRouteForInstalledRole(installedRole)
    }
  }

  if (guestOnly && auth.isAuthenticated) {
    // Each sign-in page serves one kind of account. Someone already signed in as that kind has
    // nothing to do here and goes home. Someone signed in as ANOTHER kind (an admin opening the
    // customer sign-in, a customer opening the agent one) is switching accounts: send them to the
    // page they asked for, with the old session ended, instead of back to the screen they left.
    const servedRoles = to.matched.flatMap((route) => (route.meta.loginRoles || []) as AuthUser['role'][])

    if (signInPageAction(servedRoles, auth.role) === 'go-home') {
      return dashboardFor(auth.role, auth.user?.status)
    }

    endSession()
  }

  if (!requiredRoleGroups.length) {
    return true
  }

  if (!auth.isAuthenticated) {
    return loginForPath(to.path)
  }

  if (requiredRoleGroups.some((roles) => !auth.hasAnyRole(roles))) {
    return dashboardFor(auth.role, auth.user?.status)
  }

  if (auth.user?.role === 'agent' && auth.user.status === 'pending' && !pendingOnly) {
    return { name: 'agent-onboarding' }
  }

  if (pendingOnly && auth.user?.status !== 'pending') {
    return dashboardFor(auth.role, auth.user?.status)
  }

  return true
})

export default router
